import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Heading, H2, Reviews, ReviewRating } from '../../components';

import css from './ListingPage.module.css';
import IconCard from '../../components/SavedCardDetails/IconCard/IconCard';

const SectionReviews = props => {
  const { reviews, fetchReviewsError ,country} = props;
  const intl = useIntl();
  const reviewsList = useMemo(() => (Array.isArray(reviews) ? reviews : []), [reviews]);

  const averageRating = useMemo(() => {
    if (!reviewsList.length) return 0;
    const total = reviewsList.reduce((sum, r) => sum + (r?.attributes?.rating || 0), 0);
    return total / reviewsList.length;
  }, [reviewsList]);

  const roundedRating = useMemo(() => Math.round(averageRating), [averageRating]);

  const [sortBy, setSortBy] = useState('recent');
  const [visibleCount, setVisibleCount] = useState(3);

  const sortedReviews = useMemo(() => {
    const list = reviewsList.slice();
    const getTime = r => new Date(r?.attributes?.createdAt || 0).getTime();
    if (sortBy === 'highest') {
      return list.sort((a, b) => {
        const diff = (b?.attributes?.rating || 0) - (a?.attributes?.rating || 0);
        return diff !== 0 ? diff : getTime(b) - getTime(a);
      });
    }
    if (sortBy === 'lowest') {
      return list.sort((a, b) => {
        const diff = (a?.attributes?.rating || 0) - (b?.attributes?.rating || 0);
        return diff !== 0 ? diff : getTime(a) - getTime(b);
      });
    }
    if (sortBy === 'oldest') {
      return list.sort((a, b) => getTime(a) - getTime(b));
    }
    return list.sort((a, b) => getTime(b) - getTime(a));
  }, [reviewsList, sortBy]);

  const onChangeSort = useCallback(e => setSortBy(e.target.value), []);

  // Reset visible reviews when sort changes
  useEffect(() => {
    setVisibleCount(3);
  }, [sortBy]);

  const visibleReviews = useMemo(() => sortedReviews.slice(0, visibleCount), [sortedReviews, visibleCount]);
  const canShowMore = visibleCount < sortedReviews.length;
  const handleShowMore = useCallback(() => setVisibleCount(c => Math.min(c + 5, sortedReviews.length)), [sortedReviews.length]);

  return (
    <section className={css.specializationsContainer}>
      <div>
        <div className={css.reviewHeaderwrapper}>
          <H2 className={css.reviewHeaderTitle}>
            <FormattedMessage id="ReviewsSection.title" defaultMessage="Hear From My Students" />
          </H2>
          {reviewsList?.length > 0 ?
            <div className={css.sortContainer}>
              <div className={css.sortControl}>
                <span className={css.sortIcon} aria-hidden="true"><IconCard type='sort' /></span>
                <select
                  id="reviews-sort"
                  value={sortBy}
                  onChange={onChangeSort}
                  aria-label={intl.formatMessage({ id: 'ReviewsSection.sort.aria', defaultMessage: 'Sort reviews' })}
                  className={css.sortSelect}
                >
                  <option value="recent">{intl.formatMessage({ id: 'ReviewsSection.sort.recent', defaultMessage: 'Most Recent' })}</option>
                  <option value="highest">{intl.formatMessage({ id: 'ReviewsSection.sort.highest', defaultMessage: 'Highest first' })}</option>
                  <option value="lowest">{intl.formatMessage({ id: 'ReviewsSection.sort.lowest', defaultMessage: 'Lowest first' })}</option>
                  <option value="oldest">{intl.formatMessage({ id: 'ReviewsSection.sort.oldest', defaultMessage: 'Oldest first' })}</option>
                </select>
                <span className={css.dropdownIcon} aria-hidden="true"><IconCard type='dropdown' /></span>
              </div>
            </div>
            : null}
        </div>
      </div>
      {reviewsList?.length > 0 ? (
        <div className={css.ratingContainer}>
          <span className={css.averageRating}>{averageRating.toFixed(1)}</span>
          <span className={css.ratingText}>
            <ReviewRating rating={roundedRating} className={css.stars} />
            <FormattedMessage id="ReviewsSection.basedOnReviews" defaultMessage="based on ({count} reviews)" values={{ count: reviewsList.length }} />
          </span>
        </div>
      ) : 
      <FormattedMessage id="ListingPage.noReviews" defaultMessage="No Reviews" />
      }
      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}
      <Reviews reviews={visibleReviews} country={country} />
      {canShowMore ? (
        <div className={css.showMoreContainer}>
          <button
            type="button"
            onClick={handleShowMore}
            className={css.showMoreButton}
          >
            {intl.formatMessage({ id: 'ReviewsSection.showMore', defaultMessage: 'Show More Reviews' })}
          </button>
        </div>
      ) : null}
    </section>
  );
};

export default SectionReviews;
